/* Country copy for the marketing pages. The Saudi pages are written by hand; for the other GCC countries the same
 * pages are re-worded from the country catalog (contracts/countries.json): Saudi-only terms (GOSI, Mudad, Qiwa,
 * Muqeem, Nitaqat, SAR, "Saudi"…) are replaced with the country's own (social insurance, WPS file, ministry, permits,
 * localisation programme, currency, demonym), and catalog facts that only hold in Saudi Arabia (sector quotas, wage
 * floors, SCE/SCFHS/Balady/Ajeer rules) are left out rather than restated with numbers that are not true elsewhere.
 * `localise(text, c, lang)` returns the text unchanged for Saudi Arabia. */
import type { Lang } from "../../data/types";
import { COUNTRIES, type CountryInfo } from "../../domain/country";
import { HEAT_BAN, INDUSTRIES, type Bi, type Industry, type IndustryFeature } from "../../domain/industry";

export const isSaudi = (c: CountryInfo) => c.code === "SA";

/** Country name as used inside an English sentence ("the UAE", "Oman") and its Arabic short name. */
export const inName = (c: CountryInfo): Bi => [c.code === "AE" ? "the UAE" : c.code === "SA" ? "Saudi Arabia" : c.short[0], c.short[1]];
/** Plural demonym ("Emiratis"). */
const demonyms = (c: CountryInfo) => c.demonym[0] + "s";
/** "ICP / GDRFA (Emirates ID and residence)" → "ICP / GDRFA". */
const head = (s: string) => s.split(" (")[0].trim();
export const permitsShort = (c: CountryInfo): Bi => [head(c.permits.name[0]), head(c.permits.name[1])];
/** Last word of the currency name ("UAE dirham" → "dirham") and the first Arabic word ("ريال عُماني" → "ريال"). */
const currencyWord = (c: CountryInfo): Bi => [c.currency.word[0].split(" ").pop()!, c.currency.word[1].split(" ")[0]];
/** Localisation programme without the "al-": "التعمين" → "تعمين". */
const bare = (s: string) => s.replace(/^ال/, "");

/* Arabic words are matched whole, with an optional one-letter prefix (و ب ل ف ك) kept in front of the replacement. */
const AR_LETTER = "\\u0600-\\u06FF";
function arWord(word: string): RegExp {
  return new RegExp(`(?<![${AR_LETTER}])([وبلفك]?)${word}(?![${AR_LETTER}])`, "g");
}
function enWord(word: string, flags = "g"): RegExp {
  return new RegExp(`\\b${word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, flags);
}

/** Overtime premium of the country's labour law ("125%"). */
export const overtimePct = (c: CountryInfo) => Math.round(c.overtime * 100) + "%";

type Replacer = (match: string, ...rest: (string | number)[]) => string;
type Rule = [RegExp | string, string | Replacer];

/** Replacement capitalised only at the start of a sentence (or of a title); the plural "s" is the first group. */
const sentenceCase = (word: string): Replacer => (_m, plural, offset, whole) => {
  const before = String(whole).slice(0, Number(offset)).trimEnd();
  const start = before === "" || /[.:;—–]$/.test(before);
  return (start ? word[0].toUpperCase() + word.slice(1) : word) + String(plural);
};

/** Saudi Arabia's Sunday–Thursday week becomes the country's own working week (UAE: Monday–Friday). */
function weekRules(c: CountryInfo, lang: Lang): Rule[] {
  if (c.weekend.join() === "5,6") return [];
  const DAY: Bi[] = [["Sunday", "الأحد"], ["Monday", "الاثنين"], ["Tuesday", "الثلاثاء"], ["Wednesday", "الأربعاء"], ["Thursday", "الخميس"], ["Friday", "الجمعة"], ["Saturday", "السبت"]];
  const work = [0, 1, 2, 3, 4, 5, 6].filter((d) => !c.weekend.includes(d));
  const first = DAY[work[0]], last = DAY[work[work.length - 1]];
  return lang === "ar" ? [["من الأحد إلى الخميس", `من ${first[1]} إلى ${last[1]}`]] : [["Sunday–Thursday", `${first[0]}–${last[0]}`]];
}

function enRules(c: CountryInfo): Rule[] {
  const the = inName(c)[0];
  const hb = c.heatBan;
  return [
    // whole phrases first (hand-checked wording)
    ["the certificates, Saudization quotas, shifts,", "the certificates, shifts,"],
    [" keeps government-relations work in Saudi hands as the rules require, and", " and"],
    ["who file at MISA, Qiwa, Muqeem and the embassies", "who file with the ministries, immigration and the embassies"],
    ["Delegation on Absher Business, Muqeem and Qiwa to file on the company's behalf.", "Delegation on the government e-service portals to file on the company's behalf."],
    ["health professions carry some of the highest localisation rates in the Kingdom", "health regulators audit every practitioner's licence"],
    ["the summer midday ban, engineer quotas and Professional Verification rules", "the summer midday ban and licensing rules"],
    ["against contractual headcount, Saudization and evidence requirements", "against contractual headcount and evidence requirements"],
    [" and must hit IT localisation targets with minimum wages", " and must meet localisation targets"],
    ["compete for scarce Saudi engineers", "compete for scarce engineers"],
    ["SCE membership, the 25% engineering quota with a wage floor, and supervision staff", "Engineering licences and supervision staff"],
    ["StartHR keeps registrations valid, counts the quota and costs every project.", "StartHR keeps licences valid and costs every project."],
    ["StartHR gives you the quota maths, the people data", "StartHR gives you the people data"],
    ["StartHR turns licences into a live credential wallet and shows each profession's quota.", "StartHR turns licences into a live credential wallet."],
    ["while tourism localisation now covers 41 roles with front desks fully Saudi. StartHR shows your localisation gaps by role, keeps", "and localisation targets keep rising. StartHR keeps"],
    ["Auto spare-parts retailers and wholesalers operate under one of the earliest outlet-level localisation decisions. StartHR shows the Saudi share per branch, keeps", "Auto spare-parts retailers and wholesalers run many outlets with long opening hours. StartHR keeps"],
    ["Trading companies juggle retail branches, warehouses, van sales and back-office teams, each with its own localisation rule. StartHR counts every quota, tracks", "Trading companies juggle retail branches, warehouses, van sales and back-office teams. StartHR tracks"],
    ["Branches, field sales and every localisation quota — on one dashboard.", "Branches, field sales and every branch's costs — on one dashboard."],
    ["Keep every outlet localised, staffed and commission-ready.", "Keep every outlet staffed, licensed and commission-ready."],
    ["24/7 rosters and the new tourism localisation rules — handled.", "24/7 rosters, health certificates and property costs — handled."],
    ["Hire, localise and keep engineering talent", "Hire and keep engineering talent"],
    ["SCE-registered teams, project timesheets and the engineering quota — together.", "Licensed engineering teams, project timesheets and project costs — together."],
    ["Profit depends on bill rates covering salary, GOSI, levies, Iqama, housing and end-of-service.", `Profit depends on bill rates covering salary, ${c.si.short[0]}, permit fees, housing and end-of-service.`],
    ["recruit workers and second them to clients under Ajeer permits", "recruit workers and second them to clients under staff-loan permits"],
    ["Hajj & season premium", "Peak-season premium"],
    [", and keeps procurement and sales quotas in sight.", "."],
    ["every shift covered, every quota met.", "every shift covered, every renewal on time."],
    ["Transport General Authority professional driver card.", "Professional driver card from the transport authority."],
    ["within the 50% cap", "within the legal deduction cap"],
    ["150%", overtimePct(c)],
    ["bill rates ignore levies", "bill rates ignore permit fees"],
    ["Iqama, levy & visa fees", "Residence permit, work-permit & visa fees"],
    ["Ajeer permit", "Staff-loan permit"],
    ...weekRules(c, "en"),
    ["within the Art. 92 50% cap", "within the legal deduction cap"],
    ["Art. 92 deduction cap", "legal deduction cap"],
    ["the 2025 Labor Law", "the " + c.laborLaw[0]],
    ["2025 Labor Law", c.laborLaw[0]],
    ["Labor-Law", "Labour-Law"],
    ["Labor Law", "Labour Law"],
    ["Ministry of Tourism licence", "Tourism licence"],
    ["Ministry of Tourism", "tourism authority"],
    ["Professional Verification", "trade certification"],
    ["Professional verification", "Trade certification"],
    ["SCE accreditation", "Engineering licence"],
    ["SCE registrations", "engineering licences"],
    ["SCE registration wallet", "Engineering licence wallet"],
    ["SCE membership reimbursement", "Engineering licence reimbursement"],
    ["SCE membership", "engineering licence"],
    ["SCE and operator licences", "engineering and operator licences"],
    [enWord("SCE"), "engineering licence"],
    ["SCFHS registration", "Health-professional licence"],
    ["SCFHS", "health-authority"],
    ["Balady health-certificate", "municipal health-certificate"],
    ["Balady certificate", "Municipal health certificate"],
    ["Balady professional licence", "Municipal professional licence"],
    [enWord("Balady"), "municipal"],
    ["Ajeer lending permit", "staff-loan permit"],
    ["Ajeer permits", "staff-loan permits"],
    ["work permit and Ajeer renewals", "work permit and staff-loan permit renewals"],
    ["work permit & Ajeer", "work permit & staff-loan permit"],
    [enWord("Ajeer"), "staff-loan permit"],
    ["exit re-entry visas", "visas"],
    ["exit re-entry", "visa"],
    ["12:00–15:00", `${hb.start}–${hb.end}`],
    ["Saudization (Nitaqat)", c.localisation.name[0]],
    [enWord("Saudization"), c.localisation.short[0]],
    [enWord("Nitaqat"), c.localisation.short[0]],
    ["Saudi Arabia", inName(c)[0]],
    ["the Kingdom", the],
    [enWord("KSA"), the],
    [/\ba Saudi\b/g, (/^[AEIOU]/.test(c.demonym[0]) ? "an " : "a ") + c.demonym[0]],
    [enWord("Saudis"), demonyms(c)],
    [enWord("Saudi"), c.demonym[0]],
    [enWord("GOSI"), c.si.short[0]],
    [enWord("Mudad"), "WPS"],
    [enWord("Qiwa"), c.ministry.short[0]],
    [enWord("MHRSD"), c.ministry.short[0]],
    [enWord("Muqeem"), permitsShort(c)[0]],
    ["Iqama numbers", c.nationalId.resident[0] + " numbers"],
    [/\b[Ii]qama(s?)\b/g, sentenceCase("residence permit")],
    [enWord("riyal"), currencyWord(c)[0]],
    [enWord("SAR"), c.currency.code],
  ];
}

function arRules(c: CountryInfo): Rule[] {
  const loc = c.localisation.short[1];
  const hb = c.heatBan;
  return [
    ["الشهادات ونسب التوطين والورديات", "الشهادات والورديات"],
    ["، ويُبقي أعمال التعقيب بأيدٍ سعودية كما تشترط الأنظمة", ""],
    ["ويُبقي أعمال التعقيب بأيدٍ سعودية كما تشترط الأنظمة، ", ""],
    ["يراجعون وزارة الاستثمار وقوى ومقيم والسفارات", "يراجعون الوزارات والجوازات والسفارات"],
    ["تفويض في أبشر أعمال ومقيم وقوى لإنجاز المعاملات باسم المنشأة.", "تفويض في بوابات الخدمات الحكومية لإنجاز المعاملات باسم المنشأة."],
    ["والمهن الصحية من أعلى نسب التوطين في المملكة", "وتدقق الجهات الصحية ترخيص كل ممارس"],
    ["مع حظر العمل وقت الظهيرة صيفًا ونسب توطين المهندسين واشتراطات الفحص المهني", "مع حظر العمل وقت الظهيرة صيفًا واشتراطات الترخيص"],
    ["وفق أعداد ونسب توطين ومتطلبات إثبات تعاقدية", "وفق أعداد ومتطلبات إثبات تعاقدية"],
    ["، ويلزمها تحقيق نسب توطين تقنية المعلومات بحد أدنى للأجور", "، ويلزمها تحقيق نسب " + loc],
    ["تتنافس شركات البرمجيات على المهندسين السعوديين", "تتنافس شركات البرمجيات على المهندسين"],
    ["تحدد عضوية الهيئة السعودية للمهندسين ونسبة 25% مع الحد الأدنى للأجر وفرق الإشراف", "تحدد تراخيص مزاولة الهندسة وفرق الإشراف"],
    ["يحافظ ستارت HR على سريان التسجيلات ويحتسب النسبة ويحدد تكلفة كل مشروع.", "يحافظ ستارت HR على سريان التراخيص ويحدد تكلفة كل مشروع."],
    ["يمنحك ستارت HR حسابات النسب وبيانات الموظفين", "يمنحك ستارت HR بيانات الموظفين"],
    ["يحوّل ستارت HR التراخيص إلى محفظة اعتمادات حية ويعرض نسبة كل مهنة.", "يحوّل ستارت HR التراخيص إلى محفظة اعتمادات حية."],
    ["وأصبح توطين السياحة يشمل 41 مهنة مع توطين الاستقبال بالكامل. يعرض ستارت HR فجوات التوطين حسب المهنة ويحدّث", "بينما ترتفع نسب " + loc + " باستمرار. يحدّث ستارت HR"],
    ["يعمل تجار قطع غيار السيارات بالتجزئة والجملة وفق أحد أقدم قرارات التوطين على مستوى المنفذ. يعرض ستارت HR نسبة السعوديين لكل فرع ويضمن", "يدير تجار قطع غيار السيارات بالتجزئة والجملة منافذ كثيرة بساعات عمل طويلة. يضمن ستارت HR"],
    ["توازن الشركات التجارية بين فروع التجزئة والمستودعات ومبيعات السيارات والإدارة، ولكل منها قاعدة توطين. يحتسب ستارت HR كل نسبة ويتابع", "توازن الشركات التجارية بين فروع التجزئة والمستودعات ومبيعات السيارات والإدارة. يتابع ستارت HR"],
    ["الفروع والمبيعات الميدانية وكل نسب التوطين — في لوحة واحدة.", "الفروع والمبيعات الميدانية وتكلفة كل فرع — في لوحة واحدة."],
    ["حافظ على توطين كل منفذ وتغطيته وجاهزية عمولاته.", "حافظ على تغطية كل منفذ وتراخيصه وجاهزية عمولاته."],
    ["جداول على مدار الساعة وقواعد توطين السياحة الجديدة — تحت السيطرة.", "جداول على مدار الساعة والشهادات الصحية وتكلفة المنشآت — تحت السيطرة."],
    ["وظّف ووطّن واحتفظ بالكفاءات التقنية", "وظّف واحتفظ بالكفاءات التقنية"],
    ["فرق مسجلة في الهيئة وجداول ساعات للمشاريع ونسبة توطين المهندسين — معًا.", "فرق هندسية مرخصة وجداول ساعات للمشاريع وتكلفة كل مشروع — معًا."],
    ["يعتمد الربح على تغطية سعر الفوترة للراتب والتأمينات والمقابل المالي والإقامة والسكن ونهاية الخدمة.", `يعتمد الربح على تغطية سعر الفوترة للراتب و${c.si.short[1]} ورسوم التصاريح والسكن ونهاية الخدمة.`],
    ["وتعيرهم للعملاء بتصاريح أجير", "وتعيرهم للعملاء بتصاريح إعارة العمالة"],
    ["علاوة الحج والمواسم", "علاوة المواسم"],
    ["، ويُبقي نسب توطين المشتريات والمبيعات في متناولك", ""],
    ["وكل نسبة توطين محققة", "وكل تجديد في موعده"],
    ["من الهيئة العامة للنقل", "من جهة النقل المختصة"],
    ["ضمن سقف 50%", "ضمن الحد النظامي للاستقطاع"],
    ["150%", overtimePct(c)],
    ["تتجاهل المقابل المالي", "تتجاهل رسوم التصاريح"],
    ["رسوم الإقامة والمقابل المالي والتأشيرات", "رسوم الإقامة وتصاريح العمل والتأشيرات"],
    ["شهادة الفحص المهني", "شهادة الكفاءة المهنية"],
    ["تصريح أجير", "تصريح الإعارة"],
    ...weekRules(c, "ar"),
    ["ضمن سقف 50% وفق المادة 92", "ضمن الحد النظامي للاستقطاع"],
    ["وحد الاستقطاع (م 92)", "والحد النظامي للاستقطاع"],
    ["نظام العمل 2025", c.laborLaw[1]],
    ["نظام العمل", "قانون العمل"],
    ["ترخيص وزارة السياحة", "ترخيص السياحة"],
    ["وزارة السياحة", "جهة السياحة المختصة"],
    ["اعتماد الهيئة السعودية للمهندسين", "ترخيص مزاولة الهندسة"],
    ["الهيئة السعودية للمهندسين", "جهة ترخيص المهندسين"],
    ["تسجيل الهيئة", "ترخيص الهندسة"],
    ["تسجيلات الهيئة", "تراخيص الهندسة"],
    ["رسوم عضوية الهيئة", "رسوم ترخيص الهندسة"],
    ["عضوية المهندس في الهيئة", "ترخيص المهندس"],
    ["عضوية الهيئة", "ترخيص الهندسة"],
    ["واعتماد الهيئة", "وترخيص الهندسة"],
    ["اعتماد الهيئة", "ترخيص الهندسة"],
    ["التسجيل المهني في الهيئة السعودية للتخصصات الصحية", "ترخيص مزاولة المهنة الصحية"],
    ["الهيئة السعودية للتخصصات الصحية", "الجهة الصحية المختصة"],
    ["تسجيل الممارس في الهيئة", "ترخيص الممارس"],
    ["الفحص المهني", "شهادة الكفاءة المهنية"],
    ["من بلدي", "من البلدية"],
    ["شهادة بلدي", "شهادة البلدية"],
    ["تصاريح أجير", "تصاريح إعارة العمالة"],
    [arWord("أجير"), "$1تصاريح الإعارة"],
    ["وتأشيرات الخروج والعودة", "والتأشيرات"],
    ["تأشيرات الخروج والعودة", "التأشيرات"],
    ["والخروج والعودة", "والتأشيرات"],
    ["الخروج والعودة", "التأشيرات"],
    ["12:00–15:00", `${hb.start}–${hb.end}`],
    ["التوطين (نطاقات)", c.localisation.name[1]],
    [arWord("نطاقات"), "$1" + loc],
    ["المملكة العربية السعودية", c.name[1]],
    ["في المملكة", "في " + c.short[1]],
    ["داخل المملكة", "داخل " + c.short[1]],
    [arWord("السعوديين"), "$1المواطنين"],
    [arWord("السعوديون"), "$1المواطنون"],
    [arWord("السعودية"), "$1الوطنية"],
    [arWord("السعودي"), "$1المواطن"],
    [arWord("سعوديين"), "$1مواطنين"],
    [arWord("سعوديون"), "$1مواطنون"],
    [arWord("سعودية"), "$1وطنية"],
    [arWord("سعودي"), "$1مواطن"],
    [arWord("التوطين"), "$1" + loc],
    [arWord("توطين"), "$1" + bare(loc)],
    [arWord("التأمينات"), "$1" + c.si.short[1]],
    [arWord("مدد"), "$1حماية الأجور"],
    [arWord("قوى"), "$1" + c.ministry.short[1]],
    [arWord("مقيم"), "$1" + permitsShort(c)[1]],
    [arWord("ريال"), "$1" + currencyWord(c)[1]],
    ["ر.س", c.currency.symbol[1]],
  ];
}

const RULES = new Map<string, Rule[]>();
function rules(c: CountryInfo, lang: Lang): Rule[] {
  const k = c.code + lang;
  if (!RULES.has(k)) RULES.set(k, lang === "ar" ? arRules(c) : enRules(c));
  return RULES.get(k)!;
}

/** Saudi copy re-worded for another GCC country (unchanged for Saudi Arabia). */
export function localise(text: string, c: CountryInfo, lang: Lang): string {
  if (isSaudi(c) || !text) return text;
  let s = text;
  for (const [from, to] of rules(c, lang)) {
    if (typeof from === "string") s = s.split(from).join(typeof to === "string" ? to : to(from));
    else s = typeof to === "string" ? s.replace(from, to) : s.replace(from, to as (m: string, ...a: unknown[]) => string);
  }
  return s;
}
/** Both languages of a bilingual pair. */
export const localiseBi = (b: Bi, c: CountryInfo): Bi => (isSaudi(c) ? b : [localise(b[0], c, "en"), localise(b[1], c, "ar")]);

/* ---------- industry packs ---------- */

/** Facts that only hold in Saudi Arabia (sector quotas, wage floors, Saudi-only bodies): not restated elsewhere. */
const SAUDI_ONLY = /CBAHI|Marn\b|Saudi|locali[sz]|quota|Nitaqat|wage floor|SCE\b|SCFHS|Balady|Ajeer|Musaned|Tasattur|Mu'aqqib|Tourism roles|outlet roles|Customer-service roles|Professional Verification|Expatriate share|unlicensed practitioner|transfer without consent|Ticketing|Tour guides|Hajj|Outdoor work ban/;
/** Certificate notes that describe the Saudi body's own terms (validity, grades, issuer). */
const SAUDI_NOTE = /valid 5 years|max 3 years|Membership valid/;
const GENERIC_STAT = /^(Night|Weeks|24\/7|7 days)$/;
const MON: Bi[] = [["Jan", "يناير"], ["Feb", "فبراير"], ["Mar", "مارس"], ["Apr", "أبريل"], ["May", "مايو"], ["Jun", "يونيو"], ["Jul", "يوليو"], ["Aug", "أغسطس"], ["Sep", "سبتمبر"], ["Oct", "أكتوبر"], ["Nov", "نوفمبر"], ["Dec", "ديسمبر"]];
/** "06-15" → ["15 Jun", "15 يونيو"]. */
export function monthDay(md: string): Bi {
  const [m, d] = md.split("-").map(Number);
  return [`${d} ${MON[m - 1][0]}`, `${d} ${MON[m - 1][1]}`];
}
/** The summer midday outdoor-work ban of a country (Saudi Arabia: the industry catalog's rule). */
export const heatBanOf = (c: CountryInfo) => (isSaudi(c) ? HEAT_BAN : c.heatBan);

const isQuotaFeature = (f: IndustryFeature) => f.engine === "quota" || f.engine === "/saudization";

/** An industry pack as marketed in a country. Saudi Arabia: the catalog entry itself. */
const PACKS = new Map<string, Industry>();
export function industryIn(ind: Industry, c: CountryInfo): Industry {
  if (isSaudi(c)) return ind;
  const k = c.code + ":" + ind.key;
  if (!PACKS.has(k)) PACKS.set(k, packIn(ind, c));
  return PACKS.get(k)!;
}
function packIn(ind: Industry, c: CountryInfo): Industry {
  const lo = (b: Bi) => localiseBi(b, c);
  const hb = c.heatBan;
  const [from, to] = [monthDay(hb.from), monthDay(hb.to)];
  const [lvA, lvB] = c.leave.annual;
  const countryStats: [string, Bi][] = [
    ...(ind.heat ? [[`${hb.start}–${hb.end}`, [`Outdoor work ban, ${from[0]} – ${to[0]}`, `حظر العمل المكشوف من ${from[1]} إلى ${to[1]}`]] as [string, Bi]] : []),
    ...ind.stats.filter(([v, l]) => GENERIC_STAT.test(v) && !SAUDI_ONLY.test(l[0])),
    [`${lvA} days`, lvA === lvB ? ["Paid annual leave from the first year", "إجازة سنوية مدفوعة من السنة الأولى"] : [`Annual leave, rising to ${lvB} days with service`, `إجازة سنوية ترتفع إلى ${lvB} يومًا مع الخدمة`]],
    [c.si.short[0], [`${c.demonym[0]} contributions on every payroll`, `اشتراكات ${c.si.short[1]} في كل مسير رواتب`]],
  ];
  // localisation features: one honest roadmap item naming the country's programme instead of Saudi quota maths
  let quotaShown = false;
  const features: IndustryFeature[] = [];
  for (const f of ind.features) {
    if (!isQuotaFeature(f)) { features.push({ ...f, title: lo(f.title), text: lo(f.text) }); continue; }
    if (quotaShown) continue;
    quotaShown = true;
    features.push({
      ...f, status: "planned",
      title: [`${c.localisation.short[0]} tracker`, `متابعة ${c.localisation.short[1]}`],
      text: [`Your ${c.demonym[0]} share by role, measured against the ${c.localisation.name[0]} rules for your sector.`, `نسبة المواطنين حسب المهنة مقارنةً بقواعد ${c.localisation.name[1]} في قطاعك.`],
    });
  }
  const pains = ind.pains.filter((p) => !SAUDI_ONLY.test(p[0])).map(lo);
  return {
    ...ind,
    tagline: lo(ind.tagline),
    summary: lo(ind.summary),
    stats: countryStats.slice(0, 3),
    pains: pains.length >= 2 ? pains : ind.pains.map(lo),
    shift: { ...ind.shift, note: lo(ind.shift.note), weekend: ind.shift.weekend.join() === "5,6" ? [...c.weekend] : ind.shift.weekend },
    certs: ind.certs.map((x) => ({ ...x, kind: localise(x.kind, c, "en"), ar: localise(x.ar, c, "ar"), note: x.note && !SAUDI_NOTE.test(x.note[0]) ? lo(x.note) : undefined })),
    pay: ind.pay.map(lo),
    quotas: [],
    features,
  };
}

/** Live features of a pack in a country. */
export const liveIn = (ind: Industry, c: CountryInfo) => industryIn(ind, c).features.filter((f) => f.status === "live").length;
export const industriesIn = (c: CountryInfo) => INDUSTRIES.map((i) => industryIn(i, c));

/** Sample residence / ID number in the country's format (marketing mock-ups only). */
export const SAMPLE_ID: Record<string, string> = { SA: "2456789013", AE: "784198712345671", OM: "12345678", QA: "28735601234", BH: "870512345", KW: "287051201234" };

/** Every GCC country, Saudi Arabia first. */
export const GCC = COUNTRIES;

/** Marketing pages render under this scope: their copy is already worded for the page's country (above), so the
 * app-wide re-wording of t()/L() for the signed-in company's country (lib/localText.ts) must not apply on top — it
 * would also rewrite facts that stay Saudi whatever the page (data hosted in Saudi Arabia, SAR plans, ZATCA invoices,
 * the Saudi bank account) and the country names in the switcher. Amounts on these pages are formatted explicitly. */
export const MKT_SCOPE: CountryInfo = COUNTRIES.find((c) => c.code === "SA")!;
